import React from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintFormDefinition, WintFormHookProps, WintFormKey } from "models/propTypes/wintFormPropsTypes";
import { StyleEnums } from "models/enums";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
import { useFormik } from "formik";
import useFormSubmitHelpers from "./useFormSubmitHelpers";
import connectToForm from "./connectToForm";

function useWintForm<T extends Record<string, any>>(props: WintFormDefinition<T>): WintFormHookProps<T> {
  const formik = useFormik({
    initialValues: props.defaultValues ?? {},
    onSubmit: () => Promise.resolve(),
    enableReinitialize: props.enableReinitialize ?? false,
    validationSchema: props.validationSchema,
    validateOnMount: props.validateOnMount ?? true,
    validateOnChange: props.validateOnChange ?? true,
    validateOnBlur: props.validateOnBlur ?? false,
  });

  React.useEffect(() => {
    props.onFormValidationChange && props.onFormValidationChange(formik.isValid);
  }, [formik.isValid]);

  React.useEffect(() => {
    props.onFormUpdate && props.onFormUpdate(formik.values, formik.isValid);
  }, [formik.values]);

  const { submitForm, validateForm } = useFormSubmitHelpers();

  const formHandlers: FormHandles = {
    submit: async (
      submitFunc?: (values: any, data?: any) => Promise<any>,
      validationSchema?: ObjectSchema<any>,
      noValidation?: boolean,
      data?: any,
      customValidationFailMessage?: string,
      validationFailMessageType?: StyleEnums.DisplayType,
      autoHideValidationFailMessage?: boolean
    ) => {
      const res = await submitForm(
        formik,
        submitFunc ?? props.handleSubmit ?? (async () => {}),
        validationSchema,
        noValidation,
        data,
        customValidationFailMessage,
        validationFailMessageType,
        autoHideValidationFailMessage,
        props.onFormValidationError
      );
      props.onSubmitSuccess && props.onSubmitSuccess(res);

      return res;
    },
    getValues: () => {
      return formik.values;
    },
    validate: async (validationFailMessage?: string, validationSchema?: ObjectSchema<any>) =>
      validateForm(formik, validationFailMessage, validationSchema),
    setFieldValue: (name: string, value: any) => {
      formik.setFieldValue(name, value);
    },
    setFieldError: (name: string, error: string | undefined) => {
      formik.setFieldError(name, error);
    },
    setValues: (values: any) => {
      formik.setValues({ ...formik.values, ...values }, true);
    },
    getUpdatedValues: () => {
      const values = formik.values;
      const updatedValues: Record<string, any> = {};
      if (values) {
        const allKeys = Object.keys(props.defaultValues);
        Object.keys(values).forEach((key) => {
          if (!allKeys.includes(key)) allKeys.push(key);
        });

        allKeys.forEach((key) => {
          const value = values ? values[key] : undefined;
          if (props.defaultValues[key] !== value) {
            updatedValues[key] = value;
          }
        });
      }
      return updatedValues;
    },
    getTouched: () => formik.touched,
    isDirty: () => formik.dirty,
    resetForm: formik.resetForm,
  };

  React.useImperativeHandle(props.formRef, () => formHandlers);

  return {
    wintFormProps: { onSubmit: formik.handleSubmit, onReset: formik.handleReset },
    formState: formik,
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    submitted: formik.submitCount > 0,
    submitCount: formik.submitCount,
    isSubmitting: formik.isSubmitting,
    connect: (name: WintFormKey<T>) => connectToForm(formik, name),
    setValues: formik.setValues,
    submit: formHandlers.submit,
  };
}

export default useWintForm;
